import React from 'react';
import ReactDOM from 'react-dom';
import { Router, Route, Redirect, Switch } from 'react-router';
import { RouterStore, syncHistoryWithStore } from 'mobx-react-router';
import { Provider } from 'mobx-react';
import { create } from 'mobx-persist';
import localForage from 'localforage';
import register from './registerServiceWorker';
import { AppStore } from './app/app.store';
import { StartStore } from './start/start.store';
import { App } from './app/app';
import { StartProvider } from './start/start.provider';
import { LivingRoomProvider } from './livingroom/livingroom.provider';
import { ResultsProvider } from './results/results.provider';
import { GettingStartedProvider } from './gettingstarted/gettingstarted.provider';
import { ExamplesProvider } from './examples/examples.provider';
import Transitions from './_shared/transition/transition';
import { currentAppVersion } from './appVersion';

import './polyfills';

register();

localForage.ready().catch(function() {
    /* so that webpack sees the rejected promise as handled */
    console.log('No storage!');
});

// Set up local storage
localForage.config({
    name: 'heatingtool',
    version: currentAppVersion,
    storeName: 'keyvaluepairs' // Should be alphanumeric, with underscores.
});

/**
 * Checks the app version stored in the local storage and updates it if necessary.
 * If the stored version is different from the current app version, clears the local storage,
 * resets the app store, and redirects the user to the start page if they are not on the last page.
 *
 * If a user is loading an existing submission, they will not be effected by the route change or the flush of storage.
 * As the results will be regenerated via calculations and not from local storage.
 *
 * @returns {Promise} A promise that resolves when the local storage version check and update are completed.
 */
function updateLocalStorageVersion() {
    if (process.env.NODE_ENV === 'development') {
        console.log('Checking local storage version');
    }
    return localForage.getItem('appVersion').then((storedVersion) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('Stored version: ' + storedVersion);
        }
        if (storedVersion === null || storedVersion !== currentAppVersion) {
            if (process.env.NODE_ENV === 'development') {
                console.log('Clearing local storage because of app version change');
            }
            // Clear the local storage data
            return localForage.clear().then(() => {
                // Reset the app store
                appStore.reset();

                // Only make them start again if there is no submission ID
                if (!appStore.submissionID) {
                    routerStore.history.push('/');
                }

                // Set the new app version
                return localForage.setItem('appVersion', currentAppVersion);
            });
        }
    });
}

const appStore = AppStore.getInstance(),
    routerStore = new RouterStore(),
    startStore = new StartStore(appStore),
    rootStores = {
        appStore,
        routerStore,
        startStore
    },
    hydrate = create({
        jsonify: false,
        storage: localForage
    }),
    rootEl = document.getElementById('heatingtool-root'),
    loader = document.getElementById('heatingtool-loading'),
    basename = rootEl.getAttribute('data-basename'),
    basetitle = rootEl.getAttribute('data-basetitle'),
    sectoken = rootEl.getAttribute('data-sectoken'),
    browserHistory = require('history').createBrowserHistory({ basename: basename }),
    history = syncHistoryWithStore(browserHistory, routerStore);

updateLocalStorageVersion().then(() => {
    hydrate('app', appStore).then(() => {
        console.log('Initial hydrate complete');
    });
});

appStore.baseURL = basename;
appStore.baseTitle = basetitle;
appStore.sectoken = sectoken;

ReactDOM.render(
    <Provider {...rootStores}>
        <Router history={history}>
            <Route render={({ location }) => (
                <App className='app'>
                    <Transitions pageKey={location.key} {...routerStore.history.location.state}>
                        <Switch location={location}>
                            <Route exact path='/' component={StartProvider} />
                            <Route path='/livingroom' component={LivingRoomProvider} />
                            <Route path='/result/:id?' component={ResultsProvider} />
                            <Route path='/gettingstarted' component={GettingStartedProvider} />
                            {process.env.NODE_ENV === 'development' && (
                                <Route path='/examples' component={ExamplesProvider} />
                            )}
                            <Redirect to='/' />
                        </Switch>
                    </Transitions>
                </App>
            )}/>
        </Router>
    </Provider>,
    rootEl,
    () => {
        if (loader) {
            loader.classList.add('heatingtool-loading--out');
            setTimeout(() => {
                loader.parentElement.removeChild(loader);
            }, 500);
        }
    }
);

routerStore.history.listen(location => {
    if (process.env.NODE_ENV === 'development') {
        console.log('Route changed. App store:', appStore);
        console.log(location); // debugging location
    }
});

if (module.hot) {
    module.hot.accept();
}
